/**
            * @file This file contains the main App class, which initializes the game,
            * manages various game components, and handles top-level application flow and events.
            * @module App
            */

import { UIManager } from './managers/UIManager.js';
import { SaveManager } from './managers/SaveManager.js';
import { PlayerStatsManager } from './managers/PlayerStatsManager.js';
import { BiomeManager } from './managers/BiomeManager.js';
import { CampaignManager } from './managers/CampaignManager.js';
import { Game } from './Game.js';
import { DebugManager } from './managers/DebugManager.js';

import { throttle } from './lib/utils.js';
import { ConfigManager } from './managers/ConfigManager.js';
import { mergeVillageConfig } from './config/mergeVillageConfig.js';
import { biomesConfig } from './config/biomesConfig.js';
import { textConfig } from './config/textConfig.js';
import { gameOptionsConfig } from './config/gameOptionsConfig.js';
import { miniMergeConfig } from './config/miniMergeConfig.js';
import { masterItemsConfig } from './config/masterItemsConfig.js';
import { audioConfig } from './config/audioConfig.js';
import { audioEffectsConfig } from './config/audioEffectsConfig.js';
import { AudioManager } from './managers/AudioManager.js';

import { achievementsConfig } from './config/achievementsConfig.js';
import { AchievementManager } from './managers/AchievementManager.js';
import { EventEmitter } from './lib/EventEmitter.js';

import { TestRunner } from './testing/TestRunner.js';

import { achievementTests } from './testing/achievementTests.js';
import { setupEventListeners } from './lib/eventManager.js';
import { PreferencesManager } from './managers/PreferencesManager.js';
import { 
    GAME_TYPE_QUICK_CLICK, 
    GAME_TYPE_CAMPAIGN_CLICK, 
    GAME_TYPE_CAMPAIGN_ROLLING, 
    GAME_TYPE_AUTOSAVE_QUICK,
    GAME_TYPE_AUTOSAVE_CAMPAIGN,
    GAME_TYPE_AUTOSAVE_ROLLING
} from './constants/gameTypes.js';
import { DemoManager } from './managers/DemoManager.js';
import { gameTypeDisplayNames } from './ui/viewTemplates.js';

/**
 * Description placeholder
 *
 * @export
 * @class App
 * @typedef {App}
 */
export class App {
    /**
 * Creates an instance of App.
 *
 * @constructor
 */
    constructor() {
        /** @private */
        this.eventEmitter = new EventEmitter();

        /**
         * References to key DOM elements.
         * @type {object}
         */
        this.dom = this.mapDomElements();

        this.isDemoRunning = false;
        this.demoManager = new DemoManager({
            game: this.game, // This will be assigned in _initializeManagers
            startDemoGameCallback: this.startDemoGame.bind(this),
            endDemoGameCallback: this.endDemo.bind(this),
        });

        const handleUserInteraction = () => {
            if (this.demoManager.isDemoRunning) {
                this.demoManager.stopDemo();
            } else {
                // If the timer is supposed to be running (i.e., on the intro page), reset it.
                if (!this.dom.pages.intro.classList.contains('hidden')) {
                    this.demoManager.startIdleTimer();
                }
            }
        };
        window.addEventListener('click', handleUserInteraction, true);
        window.addEventListener('keydown', handleUserInteraction, true);

        this._initializeManagers();        

        this.ui.showLevelComplete = this.ui.showLevelComplete.bind(this.ui);
        this.audioManager.initManager();
    }
    /**
     * Exposes a debug function to the global window object for console access.
     * @private
     */
    _setupDebugTools() {
        window.countAllItems = () => {
            console.log(JSON.stringify(this.game.getLiveEntityCounts(), null, 2));
        };
    }

    /**
     * Sets up the event listeners that orchestrate actions between different managers.
     * This is the central hub for application-level event handling.
     * @private
     */
    _setupAppEventListeners() {
        this.eventEmitter.on('game:batchUpdateNpcProperties', ({ npcId, managerType, updates }) => {
            const manager = managerType === 'human' ? this.game.npcManager : this.game.nonHumanNPCManager;
            if (manager) {
                manager.batchUpdateNpcProperties(npcId, updates);
                // After updating, immediately refresh the UI selector to reflect the changes.
                this.ui.updateNpcSelector(this.game._getLiveStatsAndNpcs().allNpcs);
            }
        });

        this.eventEmitter.on('game:deleteNpc', ({ npcId, managerType }) => {
            const manager = managerType === 'human' ? this.game.npcManager : this.game.nonHumanNPCManager;
            if (manager) {
                manager.deleteNpc(npcId);
                this.ui.updateNpcSelector(this.game._getLiveStatsAndNpcs().allNpcs);
            }
        });

        this.eventEmitter.on('ui:npcTagged', ({ npcId, managerType }) => {
            const manager = managerType === 'human' ? this.game.npcManager : this.game.nonHumanNPCManager;
            if (manager) {
                manager.toggleNpcTag(npcId);
            }
        });

        this.eventEmitter.on('ui:clearAllTags', () => {
            if (this.game.npcManager) this.game.npcManager.clearAllTags();
            if (this.game.nonHumanNPCManager) this.game.nonHumanNPCManager.clearAllTags();
        });

        // Listen for a request to immediately update the NPC list in the editor.
        this.eventEmitter.on('ui:requestNpcListUpdate', () => {
            this.ui.updateNpcSelector(this.game._getLiveStatsAndNpcs().allNpcs);
        });

    }


    /**
     * Initializes all manager instances and their dependencies.
     * @private
     */
    _initializeManagers() {
        const configs = { mergeVillageConfig, biomesConfig, gameOptionsConfig, masterItemsConfig, textConfig };
        /** @private */
        this.configManager = new ConfigManager(configs);
        /** @private */
        this.baseConfig = mergeVillageConfig;
        /** @private */
        this.biomesConfig = this.configManager.biomesConfig;
        /** @private */
        this.textConfig = this.configManager.textConfig;
        /** @private */
        this.gameOptions = this.configManager.gameOptionsConfig;
        /** @private */
        this.audioManager = new AudioManager({ config: audioConfig, effectsConfig: audioEffectsConfig, eventEmitter: this.eventEmitter });
        this.masterItemsConfig = this.configManager.masterItemsConfig;

        /** @private */
        this.biomeManager = new BiomeManager({
            targetElement: this.dom.appContainer,
            gridElement: this.dom.grid,
            biomes: this.biomesConfig
        });

        /** @private */
        this.saveManager = new SaveManager();
        /** @private */
        this.preferencesManager = new PreferencesManager(this.saveManager);
        /** @private */
        this.playerStatsManager = new PlayerStatsManager({
            appContainer: this.dom.appContainer,
            audioManager: this.audioManager,
            eventEmitter: this.eventEmitter
        });

        const debugDom = {
            log: this.dom.debugLog,
            clearButton: this.dom.buttons.clearDebug,
            panelContainer: this.dom.pages.debugPanelContainer
        };
        /** @private */
        this.debugManager = new DebugManager(debugDom);

        const uiDependencies = {
            dom: this.dom,
            textConfig: this.textConfig,
            biomesConfig: this.biomesConfig,
            masterItemsConfig: this.masterItemsConfig,
            baseConfig: this.baseConfig,
            gameOptions: this.gameOptions,
            playAudio: this.audioManager.play.bind(this.audioManager),
            biomeManager: this.biomeManager,
            eventEmitter: this.eventEmitter,
            playerStatsManager: this.playerStatsManager,
            preferencesManager: this.preferencesManager,
            configManager: this.configManager,
            saveManager: this.saveManager,
            achievementsConfig: achievementsConfig,
            debugManager: this.debugManager // Pass the debug manager to the UI
        };
        /** @private */
        this.ui = new UIManager(uiDependencies);

        /** @private */
        this.achievementManager = new AchievementManager({
            playerStatsManager: this.playerStatsManager,
            eventEmitter: this.eventEmitter,
            ui: this.ui,
            audioManager: this.audioManager
        });

        

        

        const campaignDependencies = {
            baseConfig: this.baseConfig,
            biomesConfig: this.biomesConfig,
            gameOptions: this.gameOptions,
            masterItemsConfig: this.masterItemsConfig
        };
        /** @private */
        this.campaignManager = new CampaignManager(campaignDependencies);

        const gameDependencies = {
            ui: this.ui,
            playerStats: this.playerStatsManager,
            campaign: this.campaignManager,
            textConfig: this.textConfig,
            configManager: this.configManager,
            debug: this.debugManager,
            audioManager: this.audioManager,
            eventEmitter: this.eventEmitter,
            overlays: this.dom.overlays,
            preferencesManager: this.preferencesManager,
            saveManager: this.saveManager
        };
        /** @private */
        this.game = new Game(gameDependencies);
        // Now that the game instance exists, we can safely initialize the UI components
        // that might depend on it or other managers.
       // this.ui.init();
        this.demoManager.game = this.game; // Assign game instance to demo manager

        /** @private */
        this.testRunner = new TestRunner({
            eventEmitter: this.eventEmitter,
            ui: this.ui,
            audioManager: this.audioManager,
            mainAchievementManager: this.achievementManager
        });
    }


    /**
     * Maps and returns references to key DOM elements used throughout the application.
     * @returns {object} An object containing references to various HTML elements.
     */
    mapDomElements() {
        return {
            appContainer: document.getElementById('app-container'),
            grid: document.getElementById('game-grid'),
            gameGridWrapper: document.getElementById('game-grid-wrapper'),
            npcOverlay: document.getElementById('npc-overlay'),
            nonHumanNPCOverlay: document.getElementById('non-human-npc-overlay'),
            weatherOverlay: document.getElementById('weather-overlay'),
            temporaryEffectsOverlay: document.getElementById('temporary-effects-overlay'),
            disasterOverlay: document.getElementById('disaster-overlay'),
            ambientAnimalOverlay: document.getElementById('ambient-animal-overlay'),
            dayNightOverlay: document.getElementById('day-night-overlay'),
            overlays: {
                banner: document.getElementById('banner-overlay'),
                tooltip: document.getElementById('tooltip-overlay'),
                tutorial: document.getElementById('tutorial-overlay'),
                ambientAnimal: document.getElementById('ambient-animal-overlay'),
                roadOverlay: document.getElementById('road-overlay'),
                obstacleOverlay: document.getElementById('obstacle-overlay')
            },
            dayNightToggleBtn: document.getElementById('day-night-toggle-btn'),
            dimmerOverlay: document.getElementById('dimmer-overlay'),
            gold: document.getElementById('gold'),
            mainScores: document.getElementById('main-scores'),
            score: document.getElementById('score'),
            levelDetailsDisplay: document.getElementById('level-details-display'),
            gameModeDisplay: document.getElementById('game-mode-display'),
            targetScoreDisplay: document.getElementById('target-score-display'),
            targetScoreValue: document.getElementById('target-score'),
            gameToolsContainer: document.getElementById('game-tools-container'),
            infoPanel: document.getElementById('info-panel'),
            messageBox: document.getElementById('message-box'),
            gemQueueBox: document.getElementById('gem-queue-box'),
            queue: document.getElementById('gem-queue'),
            gameStatsBox: document.getElementById('game-stats-box'),
            npcEditorBox: document.getElementById('npc-editor-box'),
            npcSelector: document.getElementById('npc-selector'),
            npcEditorContent: document.getElementById('npc-editor-content'),
            settlementEditorBox: document.getElementById('settlement-editor-box'),
            settlementSelector: document.getElementById('settlement-selector'),
            settlementEditorContent: document.getElementById('settlement-editor-content'),
            settlementEditorModal: document.getElementById('settlement-editor-modal'),
            settlementEditorModalTitle: document.getElementById('settlement-editor-modal-title'),
            settlementEditorModalTabs: document.getElementById('settlement-editor-modal-tabs'),
            settlementEditorModalContent: document.getElementById('settlement-editor-modal-content'),
            settlementEditorModalCloseBtn: document.getElementById('settlement-editor-modal-close-btn'),
          
            devToolsBox: document.getElementById('dev-tools-box'),
            devToolsModal: document.getElementById('dev-tools-modal'),
            devToolsModalTitle: document.getElementById('dev-tools-modal-title'),
            devToolsModalContent: document.getElementById('dev-tools-modal-content'),
            devToolsModalCloseBtn: document.getElementById('dev-tools-modal-close-btn'),
            turnCounter: document.getElementById('turn-counter'),
            gameStatsContent: document.getElementById('game-stats-content'),
            numberOfTurns: document.getElementById('number-of-turns'),
            highScoresList: document.getElementById('high-scores-list'),
            debugLog: document.getElementById('debug-log'),
            pages: {
                game: document.getElementById('game-page'),
                testCases: document.getElementById('test-cases-page'),
                guide: document.getElementById('guide-page'),
                settings: document.getElementById('settings-page'),
                quickGameSettings: document.getElementById('quick-game-settings-page'),
                campaignSettings: document.getElementById('campaign-settings-page'),
                levelComplete: document.getElementById('level-complete-page'),
                prompt: document.getElementById('prompt-page'),
                debugPanelContainer: document.getElementById('debug-panel-container'),
                newGameHub: document.getElementById('new-game-hub-page'),
                achievements: document.getElementById('achievements-page'),
                intro: document.getElementById('intro-page'),
                about: document.getElementById('about-page'),
                miniMergeSettings: document.getElementById('mini-merge-settings-page'),
            },
            buttons: {
                newGame: document.getElementById('new-game-btn'),
                guide: document.getElementById('guide-btn'),
                settings: document.getElementById('settings-btn'),
                settingsBack: document.getElementById('settings-back-btn'),
                testCases: document.getElementById('test-cases-btn'),
                about: document.getElementById('about-btn'),
                achievements: document.getElementById('achievements-btn'),
                campaign: document.getElementById('campaign-btn'),
                newGameHub: document.getElementById('new-game-hub-btn'),
                saveGame: document.getElementById('save-game-btn'),
                nextLevel: document.getElementById('next-level-btn'),
                startCampaign: document.getElementById('start-campaign-btn'),
                campaignSettingsBack: document.getElementById('campaign-settings-back-btn'),
                startGame: document.getElementById('start-game-btn'),
                achievementsBack: document.getElementById('achievements-back-btn'),
                testCasesBackToGame: document.getElementById('test-cases-back-to-game-btn'),
                promptButtons: document.getElementById('prompt-buttons'),
                muteToggle: document.getElementById('mute-toggle-btn'),
                debugToggle: document.getElementById('debug-toggle-btn'),
                clearDebug: document.getElementById('clear-debug-btn'),
                addScore100: document.getElementById('add-score-100-btn'),
                addScore1000: document.getElementById('add-score-1000-btn'),
                addScore10000: document.getElementById('add-score-10000-btn'),
                completeLevelBtn: document.getElementById('complete-level-btn'),
                triggerGodzillaBtn: document.getElementById('trigger-godzilla-btn'),
                triggerWhirlwindBtn: document.getElementById('trigger-whirlwind-btn'),
                triggerStormBtn: document.getElementById('trigger-storm-btn'),
                triggerRainBtn: document.getElementById('trigger-rain-btn'),
                triggerSnowBtn: document.getElementById('trigger-snow-btn'),
                triggerSunnyBtn: document.getElementById('trigger-sunny-btn'),
                triggerMoonBtn: document.getElementById('trigger-moon-btn'),
                triggerFogBtn: document.getElementById('trigger-fog-btn'),
                triggerCloudyBtn: document.getElementById('trigger-cloudy-btn'),
                triggerStormWeatherBtn: document.getElementById('trigger-storm-weather-btn'),
                stopWeatherBtn: document.getElementById('stop-weather-btn'),
                triggerBirdsBtn: document.getElementById('trigger-birds-btn'),
                regenerateWallsBtn: document.getElementById('regenerate-walls-btn'),
                regenerateRoadsBtn: document.getElementById('regenerate-roads-btn'),
                showSpawnableBtn: document.getElementById('show-spawnable-btn'),
                showExistingBtn: document.getElementById('show-existing-btn'),
                showRecyclableBtn: document.getElementById('show-recyclable-btn'),
                pauseNpcsBtn: document.getElementById('pause-npcs-btn'),
                                kickstartNpcsBtn: document.getElementById('kickstart-npcs-btn'),

                nextRollingTurnBtn: document.getElementById('next-rolling-turn-btn'),
                nudgeMiniMergeBtn: document.getElementById('nudge-mini-merge-btn'),
                startMiniMerge: document.getElementById('start-mini-merge-btn'),
            },
            prompt: {
                title: document.getElementById('prompt-title'),
                message: document.getElementById('prompt-message'),
                buttons: document.getElementById('prompt-buttons'),
            },
        };
    }

    /**
     * Starts the application, wiring up event listeners and checking for saved games.
     */
    start() {
        // Ensure the demo idle timer is reset when the app starts interacting.
        this.demoManager.resetIdleTimer();

        setupEventListeners(this);
        this._setupDebugTools();
        this._setupAppEventListeners();

        const autoSaveQuick = this.saveManager.getSaveSlotByType(GAME_TYPE_AUTOSAVE_QUICK);
        const autoSaveCampaign = this.saveManager.getSaveSlotByType(GAME_TYPE_AUTOSAVE_CAMPAIGN);
        const autoSaveRolling = this.saveManager.getSaveSlotByType(GAME_TYPE_AUTOSAVE_ROLLING);

        const allAutosaves = [autoSaveQuick, autoSaveCampaign, autoSaveRolling].filter(Boolean);

        if (allAutosaves.length > 0) {
            allAutosaves.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
            const latestAutosave = allAutosaves[0];
            
            this.ui.showPrompt('autosavePrompt', { autosave: latestAutosave });
        } else {
            this.ui.showInitialPageOrPrompt();
        }

        this.demoManager.startIdleTimer(); // Start the inactivity timer for demo mode.
    }


    /**
     * Continues the game from the latest autosave.
     */
    loadLatestAutosave() {
        // Ensure the demo idle timer is reset when loading a game.
        this.demoManager.resetIdleTimer();

        const autoSaveQuick = this.saveManager.getSaveSlotByType(GAME_TYPE_AUTOSAVE_QUICK);
        const autoSaveCampaign = this.saveManager.getSaveSlotByType(GAME_TYPE_AUTOSAVE_CAMPAIGN);
        const autoSaveRolling = this.saveManager.getSaveSlotByType(GAME_TYPE_AUTOSAVE_ROLLING);

        const allAutosaves = [autoSaveQuick, autoSaveCampaign, autoSaveRolling].filter(Boolean);

        if (allAutosaves.length > 0) {
            allAutosaves.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
            const latestAutosave = allAutosaves[0];
            this.loadGameByType(latestAutosave.type, latestAutosave.level);
        } else {
            console.warn('No autosave found to continue from.');
            this.ui.showPage('intro'); // Fallback to intro if no autosave
        }
    }

    /**
     * Starts a new quick game session.
     */
    startNewQuickGame() {
        // Ensure the demo idle timer is reset when starting a new game.
        this.demoManager.resetIdleTimer();

        this.biomeManager.applyBiome(this.configManager.getSettings().biomeKey);
        const gameConfig = this.configManager.getActiveGameConfig();
        this.game.initGameSession(gameConfig, false);
        this.ui.updateLevelDetailsDisplay(`${gameConfig.name} - ${gameConfig.pathLengthName}`);
        this.ui.updateTargetScore(gameConfig.targetScore);
        this.ui.updateGameModeDisplay(gameTypeDisplayNames[GAME_TYPE_QUICK_CLICK]);
        this.ui.showPage('game');

        if (this.isDemoRunning) {
            this.demoManager.run();
        }


    }
  /**
     * Starts a demo game.
     * Starts a demo game with randomized settings.
     */
    startDemoGame() {
        this.isDemoRunning = true;
        this.game.isDemoRunning = true; // Let the game instance know
        this.playerStatsManager.savingDisabled = true;

        // Select a random demo script object
        const demoConfig = this.demoManager.getRandomDemoConfig();
        this.demoManager.setCurrentDemoConfig(demoConfig);

        // 1. Randomize settings for the demo game.
        const biomeKeys = Object.keys(this.biomesConfig);
        const randomBiomeKey = biomeKeys[Math.floor(Math.random() * biomeKeys.length)];

        const availableGridSizes = this.gameOptions.gridSizes;
        const randomGridSize = availableGridSizes[Math.floor(Math.random() * availableGridSizes.length)].key;

        const availablePathLengths = this.biomesConfig[randomBiomeKey].upgradePathLinear;
        const randomPathLength = availablePathLengths[Math.floor(Math.random() * availablePathLengths.length)].key;

        // 2. Apply the randomized settings to the config manager.
        this.configManager.setBiome(randomBiomeKey);
        this.configManager.setGridSize(randomGridSize);
        this.configManager.setPathLength(randomPathLength);
        this.configManager.setDemoNpcSpawns(demoConfig.forcedAccessories || []);

        // 3. Start the new game, which will use the randomized settings.
        this.startNewQuickGame();
    }
    
    /**
     * Ends the demo game.
     */
    endDemo() {
        this.isDemoRunning = false;
        this.game.isDemoRunning = false;
        this.game.stopGameLoop();
        this.playerStatsManager.savingDisabled = false;
        this.configManager.resetToDefaults(); // Reset config for the next session.
        this.ui.showPage('intro'); // This will trigger the pageChanged event, which now handles the timer.
    }


    /**
     * Starts a new rolling game session with default settings.
     */
    startNewRollingGame() {
        // For now, a "new rolling game" is a new rolling campaign.
        const settings = { gameMode: 'rolling', isAnimalsEnabled: false }; // Animals are off in rolling mode
        this.deleteGame(GAME_TYPE_CAMPAIGN_ROLLING);
        this.startCampaignLevel(0, settings, GAME_TYPE_CAMPAIGN_ROLLING);
        /* this.biomeManager.applyBiome(this.configManager.getSettings().biomeKey);
        const gameConfig = this.configManager.getActiveGameConfig();
        this.game.initGameSession(gameConfig, false);
        this.ui.updateLevelDetailsDisplay(`${gameConfig.name} - ${gameConfig.pathLengthName}`);
        this.ui.updateTargetScore(null); // No target score for now
        this.ui.updateGameModeDisplay(gameTypeDisplayNames[GAME_TYPE_CAMPAIGN_ROLLING]);
        this._showPage('game'); */
    }

    /**
     * Loads a saved game based on its type.
     * @param {string} type - The type of game to load ('Quick' or 'Campaign').
     */
    loadGameByType(type, level = null) {
        // Ensure the demo idle timer is reset when loading a game.
        this.demoManager.resetIdleTimer();

        const savedSlot = this.saveManager.getSaveSlotByType(type, level);
        if (!savedSlot) return;

        const { gameState, level: savedLevel } = savedSlot;
        this.configManager.setGridSize(gameState.configSettings.gridSize);
        this.configManager.setBiome(gameState.configSettings.biomeKey);
        this.configManager.setPathLength(gameState.configSettings.pathLength);
        this.configManager.setGameMode(gameState.configSettings.gameMode || 'linear'); // Default to linear for older saves
        this.configManager.setAnimalsEnabled(gameState.configSettings.isAnimalsEnabled ?? true);
        this.configManager.setDisastersEnabled(gameState.configSettings.isDisastersEnabled ?? true);
        this.configManager.setRandomQueueEnabled(gameState.configSettings.isRandomQueueEnabled ?? true);
        this.configManager.setStationaryObjectsEnabled(gameState.configSettings.isStationaryObjectsEnabled ?? true);
        this.configManager.setPowerupsEnabled(gameState.configSettings.isPowerupsEnabled ?? true);
        this.configManager.setSettlersModeEnabled(gameState.configSettings.isSettlersModeEnabled ?? false);
        this.biomeManager.applyBiome(gameState.configSettings.biomeKey);

        const gameConfig = this.configManager.getActiveGameConfig();
        this.game.restoreFromState(gameState.gameData, gameConfig, savedSlot.id);
        this.campaignManager.setCurrentLevel(savedLevel);

        let gameModeText = gameTypeDisplayNames[type] || type;
        if (type === GAME_TYPE_CAMPAIGN_CLICK || type === GAME_TYPE_CAMPAIGN_ROLLING) {
            gameModeText += ` - Level ${savedLevel + 1}`;
        }
        this.ui.updateLevelDetailsDisplay(`${gameConfig.name} - ${gameConfig.pathLengthName}`);

        this.ui.updateTargetScore(gameConfig.targetScore);
        this.ui.updateGameModeDisplay(gameModeText);

        // NPC managers are restored from state, no need to init them.

        // Play music after everything is loaded
        if (gameConfig.entranceSound) {
            this.audioManager.play(gameConfig.entranceSound);
        } else {
            this.audioManager.play('level_music');
        }
        this.ui.showPage('game');
    }

    /**
     * Deletes a saved game of a specific type.
     * @param {string} type - The type of game to delete ('Quick' or 'Campaign').
     */
    deleteGame(type, level = null) {
        const savedSlot = this.saveManager.getSaveSlotByType(type, level);
        if (savedSlot) {
            this.saveManager.deleteSaveSlot(savedSlot.id);
            this.dom.appContainer.dispatchEvent(new CustomEvent('saveSlotsChanged'));
        }
    }

    /**
     * Starts a specific campaign level.
     * @param {object} settings - The settings for this campaign run.
     * @param {number} levelIndex - The index of the campaign level to start.
     * @param {string} saveType - The type of save slot to use ('Campaign' or 'RollingCampaign').
     */
    startCampaignLevel(levelIndex, settings, saveType) {
        // Ensure the demo idle timer is reset when starting a new campaign level.
        this.demoManager.resetIdleTimer();

        const gameConfig = this.campaignManager.getLevelConfig(settings, levelIndex);
        if (gameConfig) {
            this.campaignManager.setCurrentLevel(levelIndex);
            this.biomeManager.applyBiome(gameConfig.biomeKey);
            this.game.initGameSession(gameConfig, true);
            this.ui.updateTargetScore(gameConfig.targetScore);
            this.ui.updateLevelDetailsDisplay(`${gameConfig.name} - ${gameConfig.pathLengthName}`);
            const gameModeText = gameTypeDisplayNames[saveType] || saveType;
            this.ui.updateGameModeDisplay(`${gameModeText} - Level ${levelIndex + 1}`);
            this.ui.showPage('game');
            this.game.saveGame(saveType);
        }
        else {
            this.ui.displayMessage(this.textConfig.gameMessages.campaignComplete);
        }
    }

    /**
     * Starts a new campaign from the beginning.
     * @param {object} settings - The settings for the new campaign.
     */
    startNewCampaign(settings) {
        this.campaignManager.resetCampaign();
        this.startCampaignLevel(0, settings, GAME_TYPE_CAMPAIGN_CLICK);
    }

    startMiniMergeGame(settings) {
        // Ensure the demo idle timer is reset when starting a new game.
        this.demoManager.resetIdleTimer();

        const gameConfig = { ...miniMergeConfig, biomeKey: settings.biomeKey };
        this.biomeManager.applyBiome(gameConfig.biomeKey);
        this.game.initGameSession(gameConfig, false);
        this.ui.updateLevelDetailsDisplay(`Mini Merge - ${this.biomesConfig[gameConfig.biomeKey].name}`);
        this.ui.updateTargetScore(null);
        this.ui.updateGameModeDisplay('Mini Merge');
        this.ui.showPage('game');
    }

}